import { useState, useRef, useEffect } from 'react';

interface EventSelectProps {
  value: number | null;
  onChange: (value: number | null) => void;
  events: Array<{ eventId: number; displayLabel: string }>;
  disabled?: boolean;
  placeholder?: string;
}

export function EventSelect({
  value,
  onChange,
  events,
  disabled = false,
  placeholder = '-- Select an event --',
}: EventSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectedEvent = events.find(e => e.eventId === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Select Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          ref={buttonRef}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          title={selectedEvent?.displayLabel || placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '8px 10px',
            fontSize: '13px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            background: disabled ? '#f9fafb' : 'white',
            color: selectedEvent ? '#333' : '#9ca3af',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            fontFamily: 'inherit',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {selectedEvent?.displayLabel || placeholder}
          </span>
        </button>
        <span style={{
          fontSize: '13px',
          color: disabled ? '#9ca3af' : '#333',
          flexShrink: 0,
          opacity: disabled ? 0.6 : 1,
          userSelect: 'none',
          pointerEvents: 'none'
        }}>
          ▼
        </span>
      </div>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {/* Placeholder Option */}
          <div
            onClick={() => {
              onChange(null);
              setIsOpen(false);
            }}
            style={{
              padding: '8px 10px',
              fontSize: '13px',
              color: '#9ca3af',
              cursor: 'pointer',
              borderBottom: '1px solid #f3f4f6',
              backgroundColor: !value ? '#f9fafb' : 'white',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = !value ? '#f9fafb' : 'white';
            }}
          >
            {placeholder}
          </div>

          {/* Event Options */}
          {events.map((event) => (
            <div
              key={event.eventId}
              onClick={() => {
                onChange(event.eventId);
                setIsOpen(false);
              }}
              title={event.displayLabel}
              style={{
                padding: '8px 10px',
                fontSize: '13px',
                color: '#333',
                cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: value === event.eventId ? '#eff6ff' : 'white',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = value === event.eventId ? '#eff6ff' : 'white';
              }}
            >
              {event.displayLabel}
            </div>
          ))}

          {events.length === 0 && (
            <div
              style={{
                padding: '8px 10px',
                fontSize: '13px',
                color: '#9ca3af',
                textAlign: 'center',
              }}
            >
              No events available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
